import React from 'react';
import { produce } from 'immer';

import { TopOrBottomCut, TrussEnvelope } from '@paragon/contract-library';
import { absoluteVerticalCutToPlane, getTrussEnvelopeCutFacetsWithCache } from '@paragon/layout';
import { assertExhaustiveSwitch } from '@paragon/utilities';

import { useAppSelector } from '../../../../components/hooks';
import { getTrussSolidCalculationDependencies } from '../../../../library/layout-geometry/truss-envelopes/trussSolidSelectors';
import { getTargetedTrussEnvelopes } from '../../../../store/actions/layout/trussEnvelopeActions';
import { getPickingPlaneInfo } from '../../../../store/selectors/roofAndCeilingPlaneSelectors';
import { HoveredPlaneDataWithType } from '../../../../store/slices/applicationSlice';
import { layoutColors } from '../../layoutColors';
import { LayoutPolygonFaceMesh } from '../../polygon-rendering/faces/LayoutPolygonFaceMesh';

const Z_FIGHTING_PREVENTION_BUMP = 0.01;

export function CutFacets() {
  return (
    <>
      <HoveredCutFacets />
      <InProgressCutFacets />
    </>
  );
}

function HoveredCutFacets() {
  const trussEnvelopes = useAppSelector((state) => state.layout.trussEnvelopes);
  const hoveredCutFacet = useAppSelector((state) => state.layout.hoveredCutFacet);

  if (!hoveredCutFacet) return null;
  const { targetInfo, topOrBottom, cutIndex } = hoveredCutFacet;
  const targetedEnvelopes = getTargetedTrussEnvelopes(targetInfo, trussEnvelopes);

  return targetedEnvelopes.map((trussEnvelope) => (
    <BaseCutFacets
      key={trussEnvelope.guid}
      trussEnvelope={trussEnvelope}
      topOrBottom={topOrBottom}
      hoveredCutIndex={cutIndex}
    />
  ));
}

function InProgressCutFacets() {
  const trussEnvelopes = useAppSelector((state) => state.layout.trussEnvelopes);
  const pickingPlaneInfo = useAppSelector(getPickingPlaneInfo);
  if (pickingPlaneInfo == null) return null;
  const { targetInfo, hoveredPlane } = pickingPlaneInfo;

  if (targetInfo.type !== 'truss-envelope-ordered-top-cut' && targetInfo.type !== 'truss-envelope-ordered-bottom-cut')
    return null;

  const topOrBottom = targetInfo.type === 'truss-envelope-ordered-top-cut' ? 'top' : 'bottom';
  const { trussEnvelopeTargetInfo } = targetInfo;

  if (trussEnvelopeTargetInfo.target === 'insertionTrussEnvelope') return null; // The insertion envelope cannot have ordered cuts applied to it

  const targetedEnvelopes = getTargetedTrussEnvelopes(trussEnvelopeTargetInfo, trussEnvelopes);
  return targetedEnvelopes.map((envelope) => (
    <BaseCutFacets
      key={envelope.guid}
      trussEnvelope={envelope}
      topOrBottom={topOrBottom}
      hoveredCutIndex="last"
      hoveredPlane={hoveredPlane}
    />
  ));
}

function BaseCutFacets({
  trussEnvelope,
  topOrBottom,
  hoveredCutIndex,
  hoveredPlane,
}: {
  trussEnvelope: TrussEnvelope;
  topOrBottom: 'top' | 'bottom';
  hoveredCutIndex: number | 'last';
  hoveredPlane?: HoveredPlaneDataWithType | null;
}) {
  const dependencies = useAppSelector(getTrussSolidCalculationDependencies);
  const additionalCut = hoveredPlane ? getHoveredPlaneCut(hoveredPlane) : null;
  const previewTrussEnvelope = produce(trussEnvelope, (draft) => {
    if (!additionalCut) return;
    switch (topOrBottom) {
      case 'top':
        draft.orderedTopCuts.cuts.push(additionalCut);
        break;
      case 'bottom':
        draft.orderedBottomCuts.cuts.push(additionalCut);
        break;
      default:
        assertExhaustiveSwitch(topOrBottom);
    }
  });

  const cuts = getTrussEnvelopeCutFacetsWithCache(previewTrussEnvelope, dependencies);
  if (!cuts) return null;

  const cutFacets = topOrBottom === 'top' ? cuts.topCutFacets : cuts.bottomCutFacets.map((facet) => facet.reverse());
  const cutIndex = hoveredCutIndex === 'last' ? cutFacets.length - 1 : hoveredCutIndex;
  return cutFacets.map((facet, index) => (
    <LayoutPolygonFaceMesh
      key={index}
      geometry={facet.translate(facet.normalDirection.toVector(Z_FIGHTING_PREVENTION_BUMP))}
      mode="solid"
      color={
        index === cutIndex
          ? layoutColors.roofFaces.highlightHue[layoutColors.roofFaces.tints.highlighted]
          : layoutColors.roofFaces.hues.roof[layoutColors.roofFaces.tints.normal]
      }
    />
  ));
}

function getHoveredPlaneCut(hoveredPlane: HoveredPlaneDataWithType | null | undefined): TopOrBottomCut | null {
  if (hoveredPlane == null) return null;
  switch (hoveredPlane.type) {
    case 'roofPlane':
      return { type: 'RoofPlane', roofPlaneGuid: hoveredPlane.guid };

    case 'ceilingPlane':
      return { type: 'CeilingPlane', ceilingPlaneGuid: hoveredPlane.guid };

    case 'absoluteVertical':
      return {
        type: 'Absolute',
        plane: absoluteVerticalCutToPlane({ type: 'AbsoluteVertical', ...hoveredPlane.geometry }).toObject(),
      };

    default:
      assertExhaustiveSwitch(hoveredPlane);
  }
}
